'use client';

import TabControlledCarousel from './TabControlledCarousel';
import { useBloodVision } from '../BloodVisionContext';
import { useEffect, useMemo } from 'react';
import { HomeIcon, InsightsIcon, MarkerIcon, TinyTimer } from 'assets/icons';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import Image from 'next/image';
import BirthdayIcon from '../../../assets/birthday-icon.svg';
import { ImageModal } from 'components/ring-air/ProductSelectionSection/ImageModal';
import { HsaFsaOffer } from './HsaFsaOffer';
import dynamic from 'next/dynamic';
import { CarousalProps } from './carouselTypes';
import styles from './LeftPanelComponent.module.css';
const OffersCarousel = dynamic(
  () =>
    import('components/ring-air/OffersCarousel').then(
      (mod) => mod.OffersCarousel,
    ),
  { ssr: false },
);
// const ImageModal = dynamic(() => import('components/ring-air/ProductSelectionSection/ImageModal').then(mod => mod.ImageModal), { ssr: false });
// const HsaFsaOffer = dynamic(() => import('./HsaFsaOffer').then(mod => mod.HsaFsaOffer), { ssr: false });

const LeftPanelComponent = () => {
  const { width } = useWindowSize();
  const isMobile = width < 992;

  const carouselItems = [
    {
      id: '0',
      image:
        'https://public-web-assets.uh-static.com/web_v2/blood-vision/buy/desktop/car_a.png',
      icon: (
        <MarkerIcon width={isMobile ? 14 : 16} height={isMobile ? 8.5 : 10} />
      ),
      alt: 'Slide 3 description',
      text: 'Markers',
      video: getCompressedAssetUrl(
        '/web_v2/blood-vision/buy/desktop/Markers_up.mp4',
      ),
      video_mbl: getCompressedAssetUrl(
        '/web_v2/blood-vision/buy/desktop/Markers_up.mp4',
      ),
    },

    {
      id: '1',
      image:
        'https://public-web-assets.uh-static.com/web_v2/blood-vision/buy/desktop/car_a.png',
      icon: (
        <InsightsIcon width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} />
      ),
      alt: 'Slide 3 description',
      text: 'Insights',
      video: getCompressedAssetUrl(
        '/web_v2/blood-vision/buy/desktop/Clinician_Notes.mp4',
      ),
      video_mbl: getCompressedAssetUrl(
        '/web_v2/blood-vision/buy/desktop/Clinician_Notes.mp4',
      ),
    },

    {
      id: '2',
      image:
        'https://public-web-assets.uh-static.com/web_v2/blood-vision/buy/desktop/car_a.png',
      alt: 'Slide 1 description',
      icon: (
        <TinyTimer width={isMobile ? 15 : 17} height={isMobile ? 14 : 17} />
      ),
      text: 'Blood Age',
      video: getCompressedAssetUrl(
        // '/web_v2/blood-vision/buy/desktop/Final_01.mp4/w_1200',
        '/web_v2/blood-vision/buy/desktop/Reveal_01.mp4',
      ),
      video_mbl: getCompressedAssetUrl(
        // '/web_v2/blood-vision/buy/desktop/Final_01.mp4',
        '/web_v2/blood-vision/buy/desktop/Reveal_01.mp4/w_1200',
      ),
    },

    {
      id: '3',
      image:
        'https://public-web-assets.uh-static.com/web_v2/blood-vision/buy/desktop/car_a.png',
      icon: <HomeIcon width={isMobile ? 15 : 17} height={isMobile ? 14 : 17} />,
      alt: 'Slide 2 description',
      text: 'Dashboard',
      video: getCompressedAssetUrl(
        '/web_v2/blood-vision/buy/desktop/Home_01.mp4',
      ),
      video_mbl: getCompressedAssetUrl(
        '/web_v2/blood-vision/buy/desktop/Home_01.mp4',
      ),
    },
  ] satisfies CarousalProps[];

  const {
    selectedPlan,
    addOns,
    selectedAddOns,
    isCountrySupported,
    discount,
    showDiscount,
    effectiveDiscount,
    affiliateDetails,
    defaultDiscount,
  } = useBloodVision();

  const secondaryOfferData = [
    {
      title: (
        <>
          <p>
            <b>HSA/FSA</b> | Save more with your pre-tax dollars
          </p>
        </>
      ),
      modal: (
        <ImageModal>
          <HsaFsaOffer />
        </ImageModal>
      ),
    },
  ];

  // Calculate the original total price (with 799 as base)
  const originalTotal = useMemo(() => {
    if (!selectedPlan) return 799;

    // Start with the original base price of 799 instead of the discounted 499
    let originalPrice = 799;

    // Add the same add-ons prices
    selectedAddOns.forEach((addOnId) => {
      const addOn = addOns.find((addon) => addon.id === addOnId);
      if (addOn) {
        originalPrice += parseFloat(addOn.price.replace('$', ''));
      }
    });

    return originalPrice;
  }, [selectedPlan, addOns, selectedAddOns]);

  useEffect(() => {
    if (originalTotal) {
      console.log('Original Total:', originalTotal);
    }
  }, []);

  if (!isCountrySupported) {
    return null;
  }

  return (
    <>
      <div className={styles.headerInfoMobile}>
        <div className={`${styles.titleContainer} title-container`}>
          <h2 className={styles.h2}>Blood Vision</h2>
        </div>
        {/* Place currency selector here when needed */}

        <div className={styles.mblPriceContainer}>
          <div style={{ width: '100%' }}>
            <span
              style={{
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: '400',
                lineHeight: '120%',
                letterSpacing: '-0.64px',
                opacity: '0.75',
                color: '#000000',
              }}
            >
              {/* {selectedPlan?.description ||
                'Includes one comprehensive test and one follow-up, both with full blood and urine analysis.'} */}
              Comprehensive biomarker testing for metabolic, cardiovascular,
              immune, hormonal, and nutritional health, with AI-powered clinical
              summary and personalized supplement and lifestyle recommendations.
            </span>
          </div>

          {/* Discount display for mobile */}
          <div
            style={{
              marginTop: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            {/* Birthday discount tag */}
            {showDiscount && discount && (
              <p
                style={{
                  margin: '6px 0',
                  gap: '0.8rem',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  padding: '0.5rem 0.8rem',
                  borderRadius: '8px',
                  background: 'rgba(0, 0, 0, 0.04)',
                  width: 'fit-content',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: '400',
                  lineHeight: 'normal',
                }}
              >
                <Image
                  src={BirthdayIcon}
                  alt="birthday_icon"
                  style={{
                    borderRadius: '0',
                  }}
                />
                <span
                  style={{
                    opacity: '0.6',
                    color: '#000',
                    textTransform: 'none',
                    fontWeight: '400',
                  }}
                >
                  Birthday Discount Applied
                </span>
              </p>
            )}

            {/* Affiliate discount tag */}
            {effectiveDiscount.type === 'affiliate' &&
              affiliateDetails &&
              selectedPlan && (
                <p
                  style={{
                    margin: '6px 0',
                    gap: '0.8rem',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    padding: '0',
                    borderRadius: '8px',
                    background: 'transparent',
                    width: 'fit-content',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: '400',
                    lineHeight: 'normal',
                  }}
                >
                  <span
                    style={{
                      color: '#007ff5',
                      textTransform: 'uppercase',
                      fontWeight: '500',
                      fontSize: '13px',
                      letterSpacing: '1px',
                      opacity: '1',
                    }}
                  >
                    {Math.round(
                      (effectiveDiscount.amount /
                        (selectedPlan.price_split?.base_price ||
                          selectedPlan.original_price)) *
                        100,
                    )}
                    % off applied via {affiliateDetails.code}
                  </span>
                </p>
              )}

            {/* Black Friday discount tag */}
            {effectiveDiscount.type === 'black_friday' && defaultDiscount && (
              <p
                style={{
                  margin: '6px 0',
                  gap: '0.8rem',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  padding: '0.5rem 0.8rem',
                  borderRadius: '8px',
                  background: 'rgba(0, 0, 0, 0.04)',
                  width: 'fit-content',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: '400',
                  lineHeight: 'normal',
                }}
              >
                <span
                  style={{
                    opacity: '0.6',
                    color: '#000',
                    textTransform: 'none',
                    fontWeight: '400',
                  }}
                >
                  <span style={{ opacity: '1', fontWeight: '500' }}>NY26</span>{' '}
                  Offer Applied
                </span>
              </p>
            )}
          </div>
        </div>
        {secondaryOfferData && (
          <OffersCarousel data={secondaryOfferData} leftAligned={true} />
        )}
      </div>
      <div className={styles.carouselWrapper}>
        <TabControlledCarousel items={carouselItems} />
      </div>
    </>
  );
  //  (width as number) > PHONE_MAX_WIDTH_IN_PX ?

  //  : null;
};

export default LeftPanelComponent;
